import { CustomButton } from "../../../../atoms/CustomButton";
import { ModalShell } from "../../../../atoms/ModalShell";
import { useTranslation } from "react-i18next";
import { HiOutlineShare } from "react-icons/hi";
import QRCodeGenerator from "../../../../atoms/QRCodeGenerator";
import CopyToClipboardButton from "../../../../atoms/CopyToClipboardButton";
import { useId, useState } from "react";

const logoUrl = "https://cdn.1cdn.app/application/UMD/2405110558455_logo.png";

export default function ShareModal({
  showShareModal,
  setShowShareModal,
  generatedLink,
  processId,
  title,
  qrCodeTitle,
  qrCodeDescription,
}: {
  showShareModal: boolean;
  setShowShareModal: (value: boolean) => void;
  generatedLink: string;
  processId?: string;
  title?: string;
  qrCodeTitle?: string;
  qrCodeDescription?: string;
}) {
  const { t } = useTranslation();
  const linkId = useId();
  const [copy, setCopy] = useState<"idle" | "copied" | "failed">("idle");

  const handleCloseModal = () => {
    setCopy("idle");
    setShowShareModal(false);
  };
  return (
    <ModalShell
      show={showShareModal}
      onClose={handleCloseModal}
      size="md"
      dismissible
      title={
        <span className="flex items-center gap-2">
          <HiOutlineShare className="text-blue-600 flex-shrink-0" size={18} />
          {title ?? t("customRegistration.shareModal.title")}
        </span>
      }
      footer={
        <CustomButton
          variant="secondary"
          size="sm"
          onClick={handleCloseModal}
          title={t("common.close")}
        />
      }
    >
      <div className="grid gap-3 sm:gap-4 text-center">
        <div className="grid justify-items-center gap-2">
          <QRCodeGenerator
            data={generatedLink}
            size={250}
            showLogo={!!logoUrl}
            logoUrl={logoUrl}
            className="mb-2"
            ariaLabel={qrCodeTitle ?? `QR code for custom registration`}
          />
          <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-1">
            {qrCodeTitle ?? t("customRegistration.shareModal.qrCodeTitle")}
          </h3>
          <p className="text-gray-600 text-xs sm:text-sm max-w-xs sm:max-w-sm px-2 leading-tight">
            {qrCodeDescription ??
              t("customRegistration.shareModal.qrCodeDescription")}
          </p>
        </div>

        <div className="relative mx-4">
          <div className="absolute inset-0 grid items-center">
            <div className="w-full border-t border-gray-200" />
          </div>
          <div className="relative grid justify-center text-xs uppercase">
            <span className="bg-white px-2 text-gray-500">
              {t("customRegistration.shareModal.divider")}
            </span>
          </div>
        </div>

        <div className="mx-auto grid w-full max-w-sm gap-2 px-4 text-left">
          <label htmlFor={linkId} className="text-sm font-medium text-gray-700">
            {t("customRegistration.shareModal.linkLabel")}
          </label>
          <input
            id={linkId}
            type="text"
            value={generatedLink}
            readOnly
            onFocus={(e) => e.currentTarget.select()}
            className="w-full rounded-md border border-gray-300 bg-gray-50 p-2.5 text-base text-gray-700 md:py-2 md:text-sm"
          />
          <CopyToClipboardButton
            textToCopy={generatedLink}
            onSuccess={() => setCopy("copied")}
            onError={() => setCopy("failed")}
            disabled={!generatedLink}
          />
          <p
            role="status"
            className={
              copy === "failed"
                ? "text-center text-xs text-red-600"
                : "text-center text-xs text-green-700"
            }
          >
            {copy === "copied" && t("customRegistration.shareModal.copied")}
            {copy === "failed" &&
              t("customRegistration.shareModal.copyManually")}
          </p>
        </div>
      </div>
    </ModalShell>
  );
}
